import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, CheckCircle, XCircle, Zap, Clock, Paperclip, MessageSquare, Bookmark } from 'lucide-react';
import UnifiedHeader from '../../components/UnifiedHeader';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { getAssignmentForStudent, submitAssignment, getMySubmission, getMyFlags, toggleQuestionFlag } from '../../services/studioApi';
import './Studio.css';

function ProgressBar({ current, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
      <div className="progress-bar-track" style={{ flex: 1 }}>
        <div className="progress-bar-fill" style={{ width: `${(current / total) * 100}%` }} />
      </div>
      <span style={{ fontSize: '0.82rem', color: '#888', whiteSpace: 'nowrap' }}>{current}/{total}</span>
    </div>
  );
}

export default function TakeAssignment() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const submissionId = new URLSearchParams(window.location.search).get('submission');
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tStudent = t.student || {};

  const [assignment, setAssignment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [flaggedIds, setFlaggedIds] = useState(new Set());
  const startTime = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    Promise.all([
      getAssignmentForStudent(assignmentId),
      getMySubmission(assignmentId, submissionId),
      getMyFlags(),
    ]).then(([a, sub, flags]) => {
      setAssignment(a);
      setExistingSubmission(sub);
      setFlaggedIds(new Set(flags.map(f => f.question_id)));
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [assignmentId, submissionId]);

  const startTimer = (limitMinutes) => {
    setTimeLeft(limitMinutes * 60);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleStart = () => {
    startTime.current = Date.now();
    setStarted(true);
    if (assignment.time_limit_minutes) startTimer(assignment.time_limit_minutes);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const handleSubmit = async () => {
    setSubmitting(true);
    setConfirmSubmit(false);
    clearInterval(timerRef.current);
    const timeTaken = startTime.current ? Math.round((Date.now() - startTime.current) / 1000) : 0;
    try {
      const res = await submitAssignment(assignmentId, { answers, time_taken_seconds: timeTaken });
      setResults(res);
      // Load existing flags for these questions
      getMyFlags().then(flags => {
        setFlaggedIds(new Set(flags.map(f => f.question_id)));
      }).catch(() => {});
    } catch (e) { alert(e.message); }
    finally { setSubmitting(false); }
  };

  const setAnswer = (qId, val) => setAnswers(a => ({ ...a, [qId]: val }));

  if (loading) return <div className="studio-page"><div className="loading-center"><div className="spinner" /></div></div>;
  if (!assignment) return <div className="studio-page"><div className="loading-center"><p>Assignment not found</p></div></div>;

  const questions = assignment.questions || [];

  const handleToggleFlag = async (qId) => {
    try {
      const res = await toggleQuestionFlag(qId);
      setFlaggedIds(prev => {
        const next = new Set(prev);
        res.flagged ? next.add(qId) : next.delete(qId);
        return next;
      });
    } catch (e) { console.error(e); }
  };

  // Show existing submission or results
  if (results || (existingSubmission && !started)) {
    const data = results || existingSubmission;
    const pct = data.max_score > 0 ? Math.round((data.total_score / data.max_score) * 100) : data.score_percentage || 0;
    const showAnswers = data.show_answers !== false && data.show_answers_after_submission !== false;

    return (
      <div className="studio-page">
        <UnifiedHeader subRow={
          <div className="studio-subheader-left">
            <button className="studio-back-btn" onClick={() => navigate(-1)}>
              <ArrowLeft size={15} /> {t.back || 'Back'}
            </button>
            <h1 className="studio-page-title">{assignment.title} — {tStudent.results || 'Results'}</h1>
          </div>
        } />
        <div className="studio-main" style={{ maxWidth: 640 }}>
          <div className="studio-card" style={{ textAlign: 'center', marginBottom: '1.5rem', padding: '2.5rem' }}>
            {pct >= 80 && <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎉</div>}
            <div className={`score-circle ${pct >= 80 ? 'score-circle-great' : pct >= 50 ? 'score-circle-ok' : 'score-circle-low'}`}>
              <span className="score-circle-pct">{pct}%</span>
              <span className="score-circle-label">{data.total_score || 0}/{data.max_score || 0}</span>
            </div>
            <h2 style={{ margin: '0 0 0.4rem', color: '#1a1a2e' }}>
              {pct >= 80 ? (tStudent.excellent || 'Excellent!') : pct >= 50 ? (tStudent.goodJob || 'Good work!') : (tStudent.keepPracticing2 || 'Keep practicing!')}
            </h2>
            {data.mastery_updates && Object.keys(data.mastery_updates).length > 0 && (
              <p style={{ color: '#667eea', fontWeight: 600, margin: 0, fontSize: '0.9rem' }}>
                {tStudent.masteryUpdated || 'Mastery score updated!'} +{Math.round((Object.values(data.mastery_updates)[0] || 0) * 100)}%
              </p>
            )}
          </div>

          {/* Teacher feedback */}
          {data.teacher_comment && (
            <div className="studio-card" style={{ borderLeft: '4px solid #667eea', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <MessageSquare size={16} color="#667eea" />
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#5B35A0' }}>
                  {tStudent.teacherFeedback || 'Teacher Feedback'}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {data.teacher_comment}
              </p>
            </div>
          )}

          {/* Per-question feedback — ordered by assignment question order */}
          {showAnswers && data.ai_feedback && (
            <div className="studio-card">
              <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700 }}>
                {tStudent.questionBreakdown || 'Question Breakdown'}
              </h3>
              {questions.map((q, qIdx) => {
                const fb = data.ai_feedback[q.id];
                if (!fb) return null;
                const isFlagged = flaggedIds.has(q.id);
                return (
                  <div key={q.id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #f5f5f5' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.35rem' }}>
                      {fb.correct || fb.score >= 0.7
                        ? <CheckCircle size={16} color="#4caf50" style={{ flexShrink: 0, marginTop: 2 }} />
                        : <XCircle size={16} color="#f44336" style={{ flexShrink: 0, marginTop: 2 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#333' }}>
                            {tStudent.question || 'Q'}{qIdx + 1}: {q.text}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                            <span style={{ fontWeight: 600, fontSize: '0.82rem', color: fb.score >= 0.7 ? '#2e7d32' : '#c62828' }}>
                              {Math.round((fb.score || 0) * 100)}%
                            </span>
                            <button
                              title={isFlagged ? (tStudent.unflagRevision || 'Remove from revision') : (tStudent.flagRevision || 'Flag for revision')}
                              onClick={() => handleToggleFlag(q.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 1 }}
                            >
                              <Bookmark size={15} color={isFlagged ? '#f59e0b' : '#ccc'} fill={isFlagged ? '#f59e0b' : 'none'} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 0.25rem 1.5rem', fontSize: '0.85rem', color: '#555' }}>{fb.feedback}</p>
                    {fb.gaps?.length > 0 && (
                      <div style={{ marginLeft: '1.5rem', display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                        {fb.gaps.map((g, j) => <span key={j} className="badge badge-red">{g}</span>)}
                      </div>
                    )}
                    {isFlagged && (
                      <div style={{ marginLeft: '1.5rem', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <Bookmark size={11} color="#f59e0b" fill="#f59e0b" />
                        <span style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: 600 }}>
                          {tStudent.flaggedForRevision || 'Flagged for revision'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {pct < 80 && (
              <button className="btn-primary" onClick={() => { setResults(null); setExistingSubmission(null); setStarted(false); setAnswers({}); setCurrentIdx(0); }}>
                <Zap size={15} /> {tStudent.tryAgain || 'Try Again'}
              </button>
            )}
            <button className="btn-secondary" onClick={() => navigate(-1)}>
              {t.back || 'Back'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Start screen
  if (!started) {
    return (
      <div className="studio-page">
        <UnifiedHeader subRow={
          <div className="studio-subheader-left">
            <button className="studio-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={15} /> {t.back || 'Back'}</button>
            <h1 className="studio-page-title">{assignment.title}</h1>
          </div>
        } />
        <div className="studio-main" style={{ maxWidth: 480 }}>
          <div className="studio-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <h2 style={{ margin: '0 0 0.75rem', color: '#1a1a2e' }}>{assignment.title}</h2>
            {assignment.description && <p style={{ color: '#666', marginBottom: '1.25rem' }}>{assignment.description}</p>}
            {assignment.attachments?.length > 0 && (
              <div style={{ textAlign: 'left', marginBottom: '1.5rem', padding: '0.85rem 1rem', background: '#f8f9ff', borderRadius: 10, border: '1px solid #e8ecf0' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#555', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Paperclip size={13} /> {tStudent.attachments || 'Attachments'}
                </div>
                {assignment.attachments.map((att, i) => (
                  <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" download
                    style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.84rem', color: '#667eea', textDecoration: 'none', padding: '0.25rem 0' }}>
                    <Paperclip size={12} style={{ flexShrink: 0 }} />
                    <span style={{ textDecoration: 'underline' }}>{att.name}</span>
                    {att.size && <span style={{ color: '#aaa', fontSize: '0.75rem' }}>({(att.size / 1024).toFixed(0)} KB)</span>}
                  </a>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#667eea' }}>{questions.length}</div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>{tStudent.questions || 'Questions'}</div>
              </div>
              {assignment.time_limit_minutes && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#ff9800' }}>{assignment.time_limit_minutes}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>{tStudent.minutes || 'Minutes'}</div>
                </div>
              )}
            </div>
            <button className="btn-primary" style={{ padding: '0.85rem 2.5rem', fontSize: '1rem' }} onClick={handleStart}>
              {tStudent.startAssignment || 'Start Assignment'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const q = questions[currentIdx];
  const isLast = currentIdx === questions.length - 1;
  const allAnswered = questions.every(q => answers[q.id] !== undefined && String(answers[q.id]).trim() !== '');
  const qOptions = (() => {
    if (!q.options) return q.type === 'true_false' ? ['True', 'False'] : null;
    const parsed = typeof q.options === 'string' ? JSON.parse(q.options) : q.options;
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : (q.type === 'true_false' ? ['True', 'False'] : null);
  })();

  return (
    <div className="studio-page">
      <UnifiedHeader subRow={
        <>
          <div className="studio-subheader-left">
            <h1 className="studio-page-title">{assignment.title}</h1>
          </div>
          {timeLeft !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: timeLeft < 120 ? '#e53935' : '#555', fontWeight: 700 }}>
              <Clock size={16} />{fmtTime(timeLeft)}
            </div>
          )}
        </>
      } />

      <div className="studio-main" style={{ maxWidth: 640 }}>
        <ProgressBar current={currentIdx + 1} total={questions.length} />

        {assignment.attachments?.length > 0 && (
          <div style={{ marginBottom: '0.75rem', padding: '0.6rem 1rem', background: '#f8f9ff', borderRadius: 10, border: '1px solid #e8ecf0' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#888', marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <Paperclip size={12} /> {tStudent.attachments || 'Attachments'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {assignment.attachments.map((att, i) => (
                <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" download
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: '#667eea', textDecoration: 'none' }}>
                  <Paperclip size={11} />
                  <span style={{ textDecoration: 'underline' }}>{att.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="studio-card">
          <div style={{ marginBottom: '0.5rem' }}>
            <span className={`badge ${q.type === 'multiple_choice' || q.type === 'true_false' ? 'badge-blue' : q.type === 'numeric' ? 'badge-orange' : 'badge-purple'}`}>
              {q.type === 'multiple_choice' ? (tStudent.multipleChoice || 'Multiple Choice')
                : q.type === 'true_false' ? (tStudent.trueFalse || 'True / False')
                : q.type === 'numeric' ? (tStudent.numeric || 'Numeric')
                : (tStudent.openEnded || 'Open Ended')}
            </span>
          </div>
          <h3 style={{ margin: '0.75rem 0 1.5rem', fontSize: '1.05rem', color: '#1a1a2e', lineHeight: 1.5 }}>
            {q.text}
          </h3>

          {/* Multiple choice / true_false — any type with options */}
          {(q.type === 'multiple_choice' || q.type === 'true_false') && qOptions && (
            <div className="answer-options">
              {qOptions.map((opt, i) => (
                <button key={i}
                  className={`answer-option ${answers[q.id] === opt ? 'selected' : ''}`}
                  onClick={() => setAnswer(q.id, opt)}
                >
                  <div className="answer-option-indicator" />
                  <span style={{ fontSize: '0.9rem', color: '#333' }}>{opt}</span>
                </button>
              ))}
            </div>
          )}

          {/* Numeric */}
          {q.type === 'numeric' && (
            <input
              type="number"
              className="form-input"
              value={answers[q.id] || ''}
              onChange={e => setAnswer(q.id, e.target.value)}
              placeholder={tStudent.enterNumber || 'Enter your answer...'}
              style={{ fontSize: '1.1rem', textAlign: 'center' }}
            />
          )}

          {/* Open ended — anything that isn't options-based or numeric */}
          {q.type !== 'multiple_choice' && q.type !== 'true_false' && q.type !== 'numeric' && (
            <textarea
              className="form-textarea"
              rows={5}
              value={answers[q.id] || ''}
              onChange={e => setAnswer(q.id, e.target.value)}
              placeholder={tStudent.writeAnswer || 'Write your answer here...'}
            />
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0}>
            <ChevronLeft size={16} /> {tStudent.previous || 'Previous'}
          </button>
          {isLast ? (
            <button className="btn-primary" onClick={() => setConfirmSubmit(true)} disabled={submitting}>
              <CheckCircle size={16} /> {submitting ? (tStudent.submitting || 'Submitting...') : (tStudent.submit || 'Submit')}
            </button>
          ) : (
            <button className="btn-primary" onClick={() => setCurrentIdx(i => i + 1)}>
              {tStudent.next || 'Next'} <ChevronRight size={16} />
            </button>
          )}
        </div>

        {/* Question dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
          {questions.map((q2, i) => (
            <button key={i} onClick={() => setCurrentIdx(i)} style={{
              width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: i === currentIdx ? '#667eea' : answers[q2.id] ? '#4caf50' : '#e8ecf0',
              color: i === currentIdx || answers[q2.id] ? 'white' : '#888',
              fontSize: '0.75rem', fontWeight: 700
            }}>{i + 1}</button>
          ))}
        </div>
      </div>

      {/* Confirm modal */}
      {confirmSubmit && (
        <div className="studio-modal-overlay">
          <div className="studio-modal" style={{ maxWidth: 380, textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 0.75rem' }}>{tStudent.confirmSubmitTitle || 'Submit Assignment?'}</h3>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              {allAnswered
                ? (tStudent.allAnswered || 'All questions answered.')
                : (tStudent.unanswered || `${questions.filter(q2 => !answers[q2.id]).length} question(s) unanswered.`)}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button className="btn-secondary" onClick={() => setConfirmSubmit(false)}>{t.cancel || 'Cancel'}</button>
              <button className="btn-primary" onClick={handleSubmit}>{tStudent.submit || 'Submit'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
